import { Children, useEffect, useRef, useState } from 'react';
import { DisclosureNextBreadcrumb } from '@sas/react-svg';
import cx from 'classnames';
import { useRefArray } from '../../hooks/useRefArray';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { useFormatter } from '../../locale/formatting';
import { postfixTestId } from '../../util/testId';
import { BusyStateIndicator } from '../BusyStateIndicator';
import { Icon } from '../Icon';
import { Scrollable } from '../Scrollable';
import { GroupToken, TokenDesign, TokensContextProvider } from '../Token';
import { useTokenizerFocusState, useTokenizerScrollState } from './__internal__';
import bundle from './NovaComponents-Tokenizer-gui-icu.properties';
import cn from './Tokenizer.css.json';
import './Tokenizer.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
export const Tokenizer = props => {
  const localize = useTextLocalizer();
  const formatter = useFormatter();
  const {
    createSetRef: createTokenRef
  } = useRefArray();
  const containerRef = useRef(null);
  const refChild = useRef(null);
  const [tokenMaxWidth, setTokenMaxWidth] = useState();
  const [focusedState, setFocusedState] = useState('unfocused');
  const [setScrollableContentRef, getScrollableContent] = useResizeObserverRef(() => {
    const scrollableContent = getScrollableContent();
    if (!scrollableContent) return;
    setTokenMaxWidth(scrollableContent.clientWidth);
  });
  let tokens;
  if (props.groupEnabled) {
    const groupedTokens = {};
    const groupingThreshold = props.groupingThreshold && props.groupingThreshold > 1 ? props.groupingThreshold : 20;
    const aTokenCount = {};
    const aTokens = [];
    // Group tokens based on groupId
    // Note: tokens without groupId or groupId being empty string are grouped together

    // count the token count for each group id
    for (const token of props.tokens) {
      const {
        groupId = ''
      } = token;
      if (!aTokenCount[groupId]) {
        aTokenCount[groupId] = {
          groupTokenCount: 1
        };
      } else {
        aTokenCount[groupId].groupTokenCount++;
      }
    }
    props.tokens.forEach((token, i) => {
      const {
        groupId = ''
      } = token;
      const iTokenCount = aTokenCount[groupId].groupTokenCount;
      if (iTokenCount >= groupingThreshold) {
        if (!groupedTokens[groupId]) {
          groupedTokens[groupId] = [];
        }
        groupedTokens[groupId].push({
          token,
          originalIndex: i
        });
        if (!aTokens.includes(groupId)) {
          aTokens.push(groupId);
        }
      } else {
        aTokens.push({
          token,
          originalIndex: i
        });
      }
    });
    const onRemoveTokens = (groupIdIndex, tokenIndex) => {
      if (!props.onRemoveTokens) {
        return;
      }
      const indices = [];
      let startIndex = groupIdIndex;
      const endIndex = props.hasBreadcrumbs ? aTokens.length : groupIdIndex + 1;
      if (tokenIndex !== undefined) {
        const tokenObj = aTokens[groupIdIndex];
        if (typeof tokenObj === 'string') {
          const groupTokens = groupedTokens[tokenObj]; // tokenObj is the groupId
          indices.push(groupTokens[tokenIndex].originalIndex);
        } else {
          indices.push(tokenObj.originalIndex);
        }
        startIndex = groupIdIndex + 1;
      }
      for (let i = startIndex; i < endIndex; i++) {
        const token = aTokens[i];
        if (typeof token === 'object') {
          indices.push(token.originalIndex);
        } else {
          // token is the groupId
          for (const {
            originalIndex
          } of groupedTokens[token]) {
            indices.push(originalIndex);
          }
        }
      }
      props.onRemoveTokens(indices);
    };
    tokens = aTokens.map((token, i) => {
      let groupId;
      let groupTokens = [];
      if (typeof token === 'object') {
        groupId = token.token.groupId;
        groupTokens.push(token);
      } else {
        groupId = token;
        groupTokens = groupedTokens[groupId];
      }
      const className = props.disabled ? cx(cn.token, props.disabled && cn.disabled) : cn.token;
      // Only append aria info when not focused or being focused for the first time
      // so that those info is only annouced once when nagivating within tokens
      const shouldAppendAriaInfo = focusedState !== 'focusedMoreThanOnce';
      const ariaLabels = shouldAppendAriaInfo && props.ariaLabel ? [props.ariaLabel] : [];
      const iTokenCount = aTokenCount[groupId].groupTokenCount;
      if (iTokenCount < groupingThreshold) {
        const token = groupTokens[0].token;
        ariaLabels.push(token.ariaLabel ?? token.value);
        const tokenNode = /*#__PURE__*/_jsx(TokenDesign, {
          testId: postfixTestId(props.testId, 'token' + `${i}`),
          ref: createTokenRef(i),
          className: className,
          disabled: props.disabled // this will add aria-diabled to the individual token, needed to make sure remove button is  also disabled
          ,
          onClose: () => {
            if (props.disabled) {
              return;
            }
            props.onCloseToken?.(token);
            onRemoveTokens(i);
          },
          value: token.value,
          icon: token.icon,
          tooltip: token.tooltip,
          menuSections: token.menuSections,
          includeDropdown: !!token.menuSections || token.includeDropdown,
          onItemSelected: token.onItemSelected,
          ariaLabel: ariaLabels.join(', '),
          ariaDescribedBy: shouldAppendAriaInfo ? props.ariaDescribedBy : undefined,
          readOnly: token.readOnly,
          variant: props.variant,
          id: token.id,
          dndPrefix: token.dndPrefix,
          allowDrag: token.allowDrag,
          onDidDrop: token.onDidDrop,
          isPopoverOpen: token.isPopoverOpen,
          buttonProps: token.buttonProps,
          closeButtonProps: token.closeButtonProps,
          index: i,
          preventScrollOnFocus: !props.wrap
        }, groupId);
        return token.busyStateIndicatorProps ? /*#__PURE__*/_createElement(BusyStateIndicator, {
          delay: 0,
          ...token.busyStateIndicatorProps,
          key: groupId,
          containerClassName: cx(cn.busyStateContainer, token.busyStateIndicatorProps.containerClassName)
        }, tokenNode) : tokenNode;
      } else {
        const label = props.groupLabelMap?.[groupId] ?? groupId;
        const labelCount = props.groupLabelCustomCountMap?.[groupId] ?? groupTokens.length;
        const formattedCount = formatter?.formatNumeric(labelCount) ?? labelCount;
        ariaLabels.push(`${label} (${formattedCount})`);
        return /*#__PURE__*/_jsx(GroupToken, {
          testId: postfixTestId(props.testId, label),
          ref: createTokenRef(i),
          className: className,
          disabled: props.disabled,
          index: i,
          onClose: () => {
            if (props.disabled) {
              return;
            }
            props.onCloseGroup?.(groupId);
            onRemoveTokens(i);
          },
          onCloseItem: index => {
            if (props.disabled) {
              return;
            }
            props.onCloseToken?.(groupTokens[index].token);
            onRemoveTokens(i, index);
          },
          label: label,
          ariaLabel: ariaLabels.join(', '),
          ariaDescribedBy: shouldAppendAriaInfo ? props.ariaDescribedBy : undefined,
          values: groupTokens.map(({
            token: {
              value
            }
          }) => value)
          // FIXME: GroupToken should be readOnly only if all of the tokens are readOnly, but
          //      | GroupToken does not support items with different readOnly values.
          ,
          readOnly: groupTokens.some(({
            token: {
              readOnly
            }
          }) => readOnly),
          variant: props.variant,
          showMoreThreshold: props.groupTokenShowMoreThreshold,
          onMorePress: props.onMorePress ? () => {
            props.onMorePress?.(groupId);
          } : undefined,
          preventScrollOnFocus: !props.wrap,
          customLabelCount: props.groupLabelCustomCountMap?.[groupId]
        }, groupId);
      }
    });
  } else {
    tokens = props.tokens.map((token, i) => {
      // Only append aria info when not focused or being focused for the first time
      // so that those info is only annouced once when nagivating within tokens
      const shouldAppendAriaInfo = focusedState !== 'focusedMoreThanOnce';
      const tokenNode = /*#__PURE__*/_jsx(TokenDesign, {
        ref: createTokenRef(i),
        testId: postfixTestId(props.testId, token.value),
        className: cx(cn.token, props.disabled && cn.disabled),
        value: token.value,
        icon: token.icon,
        tooltip: token.tooltip,
        menuSections: token.menuSections,
        includeDropdown: !!token.menuSections || token.includeDropdown,
        onItemSelected: token.onItemSelected,
        disabled: props.disabled // this will add aria-diabled to the individual token, needed to make sure remove button is  also disabled
        ,
        ariaLabel: shouldAppendAriaInfo && props.ariaLabel ? [token.ariaLabel ?? token.value, props.ariaLabel].join(', ') : token.ariaLabel,
        ariaDescribedBy: shouldAppendAriaInfo ? props.ariaDescribedBy : undefined,
        readOnly: token.readOnly,
        onClose: () => {
          if (props.disabled) {
            return;
          }
          props.onCloseToken?.(token);
          const indices = [];
          const endIndex = props.hasBreadcrumbs ? props.tokens.length : i + 1;
          for (let ii = i; ii < endIndex; ii++) {
            indices.push(ii);
          }
          props.onRemoveTokens?.(indices);
        },
        variant: props.variant,
        id: token.id,
        dndPrefix: token.dndPrefix,
        allowDrag: token.allowDrag,
        onDidDrop: token.onDidDrop,
        index: i,
        isPopoverOpen: token.isPopoverOpen,
        buttonProps: token.buttonProps,
        closeButtonProps: token.closeButtonProps,
        preventScrollOnFocus: !props.wrap
      }, i);
      return token.busyStateIndicatorProps ? /*#__PURE__*/_createElement(BusyStateIndicator, {
        delay: 0,
        ...token.busyStateIndicatorProps,
        key: i,
        containerClassName: cx(cn.busyStateContainer, token.busyStateIndicatorProps.containerClassName)
      }, tokenNode) : tokenNode;
    });
  }
  const {
    focusedIndex,
    tokensContextValue,
    onContainerBlur,
    onInputFocus
  } = useTokenizerFocusState({
    maxIndex: tokens.length - 1,
    focusedIndexState: props.focusedIndexState
  });
  const {
    scrollIndex,
    setScrollIndex
  } = useTokenizerScrollState({
    focusedIndex,
    tokensLength: tokens.length,
    containerRef,
    originalTokensLength: props.tokens.length
  });
  useEffect(() => {
    if (focusedIndex === undefined) {
      setFocusedState('unfocused');
    } else {
      setFocusedState(prev => prev === 'unfocused' ? 'focusedOnce' : 'focusedMoreThanOnce');
    }
  }, [focusedIndex]);
  const a11yAttributes = {
    role: props.role === '' ? undefined : props.role ?? 'grid',
    // Do not set aria-label if there is no role set
    'aria-label': props.role === '' ? undefined : props.ariaLabel ?? localize('tokenizer.default.aria'),
    'aria-labelledby': props.ariaLabelledBy
  };
  return /*#__PURE__*/_jsxs("div", {
    ...(!!props.wrap && a11yAttributes),
    ref: containerRef,
    onBlur: onContainerBlur,
    "data-testid": props.testId,
    className: cx(cn.container, props.wrap && cn.wrap, props.className),
    style: {
      // token max-width is determined this way because the CSS min has odd behavior when the tokens are in Scrollable.
      // When tokenMaxWidth is undefined, the scss file calculates a default using min().  tokenMaxWidth should be undefined
      // whenever props.wrap is falsy.
      '--token-max-width': tokenMaxWidth && `${Math.min(remToPx(25), tokenMaxWidth)}px`,
      '--tokenizer-max-lines': props.maxLines ?? 3.5,
      ...props.style
    },
    children: [/*#__PURE__*/_jsx(TokensContextProvider, {
      value: tokensContextValue,
      children: props.wrap ? tokens : /*#__PURE__*/_jsx(Scrollable, {
        contentProps: a11yAttributes,
        currentIndex: scrollIndex,
        setCurrentIndex: setScrollIndex,
        testId: postfixTestId(props.testId, 'Scroll'),
        separator: props.hasBreadcrumbs ? /*#__PURE__*/_jsx(Icon, {
          icon: DisclosureNextBreadcrumb,
          className: cn.separator
        }) : null,
        contentRef: setScrollableContentRef,
        children: tokens
      })
    }), Children.count(props.children) > 0 && /*#__PURE__*/_jsx("div", {
      className: cn.childrenContainer,
      ref: refChild,
      onFocus: onInputFocus,
      role: "row",
      children: props.children
    })]
  });
};
const remToPx = rem => rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
//# sourceMappingURL=Tokenizer.js.map